import { cn, Stack } from 'component-library';
import { FC, HTMLAttributes, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { PlotReportFactLabel, PlotReportFactLabelNameEnum, ValueClass } from '@/api/rest/resources/types/fact';
// TODO: MVP-5260 - Bring Confidence Interval back to charts
// import { RenderConfidenceInterval } from './ConfidenceInterval';
// import { useConfidenceIntervalLegendItem } from './hooks/useConfidenceIntervalLegendItem';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { Logger } from '@/lib/logs/logger';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { hasLabel } from '@/pages/shared/hooks/utils';
import { DEFAULT_TIME_FRAME } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/impact-projections-v2/constants';
import { isNullish } from '@/utils/booleans';
import { printMonthYear } from '@/utils/formatting/date';
import { getColorForIndicator } from '@/utils/getColorForIndicator';

import { BenchmarkPill } from '../Pill/BenchmarkPill';
import { ValueClassPill } from '../Pill/ValueClassPill';
import { Area, Chart, ComposedChart, getXAxisProps, TooltipProps } from './components/Chart';
import { CHART_ZOOM } from './components/ChartZoom';
import { CustomEstimationDot } from './components/CustomEstimationDot';
import { ChartLegendBar } from './components/LegendBar';
import {
  ChartTooltip,
  // ChartTooltipConfidenceInterval,TODO: MVP-5260 - Bring Confidence Interval back to charts
  ChartTooltipDot,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
import { useEstimatedDataLegendItem } from './hooks/useEstimatedDataLegendItem';
import { useYAxisDomainFromMouseWheelZoom } from './hooks/useYAxisDomainFromMouseWheelZoom';
import { NoChartData } from './NoChartData';
import { ChartDataItem, extrapolateReferenceLinesChartData, RenderReferenceLine } from './ReferenceLines';
import { ChartProps, FactValue } from './types';
import {
  getChartDataFromGraphFact,
  getDataDefaultDomain,
  getXAxisDomainForScenarioGraph,
  hasInterpolatedValues,
  minMaxAxisDomain,
  sliceSortedChartDataToDateRange,
  sortChartData,
} from './utils';
import { getChartZoomedDomain } from './utils/getChartZoomedDomain';

const waterColors = getColorForIndicator(INDICATOR.WATER_HIGH_RESOLUTION);

const chartColors = {
  whc: waterColors,
  whc_historic: waterColors,
  confidenceInterval: {
    fill: waterColors.confidenceInterval,
  },
  estimatedData: {
    stroke: waterColors.estimatedData,
  },
} as const;

const totalFactNames = {
  water_holding_capacity: 'whc_historic',
  water_holding_capacity_potential: 'whc',
  water_fc_total_benchmark_low: 'whc_benchmark_low',
  water_fc_total_benchmark_moderate: 'whc_benchmark_moderate',
  water_fc_total_benchmark_high: 'whc_benchmark_high',
};

const perHaFactNames = {
  water_holding_capacity_per_ha: 'whc_historic',
  water_holding_capacity_potential_per_ha: 'whc',
  water_fc_per_ha_benchmark_low: 'whc_benchmark_low',
  water_fc_per_ha_benchmark_moderate: 'whc_benchmark_moderate',
  water_fc_per_ha_benchmark_high: 'whc_benchmark_high',
};

const intrapolateNullValues = (data: ChartDataItem[]) => {
  const interpolatedData = structuredClone(data);
  const historicWhcYearlyValues: Record<string, FactValue | null> = {};

  Object.entries(interpolatedData).forEach(([, currentData]) => {
    historicWhcYearlyValues[new Date(currentData.label).getFullYear().toString()] = currentData.whc_historic ?? null;
  });
  Object.entries(interpolatedData).forEach(([date, currentData]) => {
    /**
     * Science spec -
     * whc is produced as 1 value per year, constant throughout the year, sm can have multiple values in a year
     * we use this whc value to fill in whc for all sm datapoints of that year that do not have a coninciding whc datapoint
     * so that the tooltip does not show "No data" for whc since whc is null for those dates
     * but as shown in the graph the step chart for whc would indicate a corresponding whc value
     */
    const defaultHistoricWhcValue =
      currentData.whc_historic ?? historicWhcYearlyValues[new Date(currentData.label).getFullYear().toString()];

    const hasInterpolatedLabels = hasLabel(
      { [PlotReportFactLabelNameEnum.value_class]: ValueClass.interpolated },
      currentData?.whc_historic?.labels ?? [],
    );

    interpolatedData[Number(date)] = {
      ...interpolatedData[Number(date)],
      whc_historic: {
        ...(defaultHistoricWhcValue ?? null),
        hasInterpolatedLabels,
      },
    };
  });
  return interpolatedData;
};

const zoomMultipliers = {
  min: 0.97,
  max: 1.03,
};

export const WaterHoldingScenarioChart: FC<HTMLAttributes<HTMLDivElement> & ChartProps & { timeFrame: number }> = ({
  analysisType,
  data,
  benchmarks,
  timeFrame = Number(DEFAULT_TIME_FRAME),
  className,
  showTooltip,
  zoom,
  setZoom,
  ...delegated
}) => {
  const { t } = useTranslation();

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const defaultDomain = getDataDefaultDomain(data, zoomMultipliers.min, zoomMultipliers.max);
  const yAxisDomainFromMouseWheel = useYAxisDomainFromMouseWheelZoom(chartContainerRef, defaultDomain, setZoom);

  const styles = cn('flex w-full flex-col', className);
  const unit = analysisType === 'total' ? t('global.ui.unit.m^3') : t('global.ui.unit.m^3/ha');

  const factNames = analysisType === 'total' ? totalFactNames : perHaFactNames;
  const dateDataMap = getChartDataFromGraphFact(data, factNames);
  const sortedChartData = sortChartData(dateDataMap, (date) => new Date(date).getTime());
  const interpolatedData = intrapolateNullValues(sortedChartData);

  const minDate = null;
  const maxDate = new Date(`${new Date().getFullYear() + timeFrame}`);
  const slicedData = sliceSortedChartDataToDateRange(interpolatedData, minDate, maxDate);

  const firstTick = slicedData.at(0);
  const lastTick = slicedData.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render chart');
    return (
      <div className={cn(styles, 'h-full justify-center')} {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const xTicksDomain = getXAxisDomainForScenarioGraph(firstTick.label, lastTick.label, timeFrame);
  const xAxisProps = getXAxisProps(xTicksDomain);

  let chartData = extrapolateReferenceLinesChartData(xTicksDomain, slicedData, 'whc_benchmark_low');
  chartData = extrapolateReferenceLinesChartData(xTicksDomain, chartData, 'whc_benchmark_moderate');
  chartData = extrapolateReferenceLinesChartData(xTicksDomain, chartData, 'whc_benchmark_high');

  const hasInterpolated = hasInterpolatedValues(chartData, 'whc_historic');

  const yAxisDomainFromButtons = getChartZoomedDomain({
    data,
    zoom,
    defaultDomain: minMaxAxisDomain(zoomMultipliers.min, zoomMultipliers.max),
  });

  const yAxisDomain = zoom === CHART_ZOOM.MOUSE_WHEEL ? yAxisDomainFromMouseWheel : yAxisDomainFromButtons;

  return (
    <>
      <Chart.Container
        ref={chartContainerRef}
        {...delegated}
        data={chartData}
        config={{
          whc: {
            label: t('shared.projects.project.impactProjections.labels.optimizedScenario'),
            color: chartColors.whc.stroke,
          },
          whc_historic: {
            label: t('shared.projects.project.impactProjections.labels.historicWaterHoldingCapacity'),
            color: chartColors.whc_historic.stroke,
          },
        }}
      >
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id='whc' x1='0' y1='-0.86' x2='0' y2='1'>
              <stop offset='0%' stopColor={chartColors.whc.fill} stopOpacity={1} />
              <stop offset='100%' stopColor={chartColors.whc.fill} stopOpacity={0} />
            </linearGradient>
            <linearGradient id='whc_historic' x1='0' y1='-0.86' x2='0' y2='1'>
              <stop offset='0%' stopColor={chartColors.whc_historic.fill} stopOpacity={1} />
              <stop offset='100%' stopColor={chartColors.whc_historic.fill} stopOpacity={0} />
            </linearGradient>
          </defs>
          {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
          {/* {RenderConfidenceInterval({
            dataKey: 'whc_historic.confidence_interval',
            type: 'stepBefore',
            fill: chartColors.confidenceInterval.fill,
            id: 'whc-historic-scenario-confidence-interval',
          })} */}
          <Area
            type='stepBefore'
            stroke='var(--color-whc_historic)'
            strokeWidth={2}
            fill='url(#whc_historic)'
            dataKey='whc_historic.value'
            connectNulls
            activeDot={ChartTooltipDot}
            dot={(props) => <CustomEstimationDot {...props} color={chartColors.estimatedData.stroke} />}
            id='whc-historic-scenario-area'
          />
          {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
          {/* {RenderConfidenceInterval({
            dataKey: 'whc.confidence_interval',
            type: 'monotone',
            fill: chartColors.confidenceInterval.fill,
            id: 'whc-scenario-confidence-interval',
          })} */}
          <Area
            type='monotone'
            isAnimationActive={false}
            stroke='var(--color-whc)'
            strokeWidth={2}
            strokeDasharray='4 4'
            fill='url(#whc)'
            fillOpacity={1}
            dataKey='whc.value'
            connectNulls
            activeDot={ChartTooltipDot}
            id='whc-scenario-area'
          />
          {RenderReferenceLine({
            dataKey: 'whc_benchmark_low.value',
            type: 'monotone',
            label: chartData.some((point) => !!point.whc_benchmark_low) ? t('global.benchmark.low') : null,
            chartData,
            id: 'whc-scenario-benchmark-low',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'whc_benchmark_moderate.value',
            type: 'monotone',
            label: chartData.some((point) => !!point.whc_benchmark_moderate) ? t('global.benchmark.moderate') : null,
            chartData,
            id: 'whc-scenario-benchmark-moderate',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'whc_benchmark_high.value',
            type: 'monotone',
            label: chartData.some((point) => !!point.whc_benchmark_high) ? t('global.benchmark.high') : null,
            chartData,
            id: 'whc-scenario-benchmark-high',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          <Chart.XAxis {...xAxisProps} className='whc-scenario-x-axis' />
          <Chart.YAxis domain={yAxisDomain} className='whc-scenario-y-axis' allowDataOverflow={true}>
            <Chart.Label>{unit}</Chart.Label>
          </Chart.YAxis>
          {showTooltip && (
            <Chart.Tooltip
              content={(props) => <CustomTooltip {...props} unit={unit as UnitEnum} benchmarks={benchmarks} />}
            />
          )}
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar showEstimate={hasInterpolated} />
    </>
  );
};

const CustomLegendBar = ({ showEstimate }: { showEstimate: boolean }) => {
  const { t } = useTranslation();
  // TODO: MVP-5260 - Bring Confidence Interval back to charts
  // const confidenceIntervalLegendItem = useConfidenceIntervalLegendItem({
  //   confidenceIntervalColor: chartColors.confidenceInterval.fill,
  // });

  const estimatedDataLegendItem = useEstimatedDataLegendItem({
    estimatedDataColor: chartColors.estimatedData.stroke ?? '',
  });

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t('shared.projects.project.impactProjections.labels.historicWaterHoldingCapacity'),
      color: chartColors.whc_historic.fill,
    },
    ...(showEstimate ? estimatedDataLegendItem : []),
    {
      label: t('shared.projects.project.impactProjections.labels.optimizedScenario'),
      color: chartColors.whc.fill,
      shape: (
        <span className='w-[14px] border-y-[3.5px] border-dotted' style={{ borderColor: chartColors.whc.stroke }} />
      ),
    },
    // ...confidenceIntervalLegendItem,
  ];

  return <ChartLegendBar items={legendBarItems} data-testid='water-holding-capacity-chart-legend' />;
};

const CustomTooltip = ({
  active,
  payload,
  label,
  unit,
  benchmarks,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
TooltipProps<ValueType, NameType> & { unit: UnitEnum; benchmarks?: any[] }) => {
  const { t } = useTranslation();

  const whcScenarioValue = payload?.find((data) => data.dataKey === 'whc.value');
  const whcHistoricValue = payload?.find((data) => data.dataKey === 'whc_historic.value');

  const whcValue = whcScenarioValue ?? whcHistoricValue;

  const whcDisplay = !isNullish(whcValue?.value)
    ? `${getDisplayNumber(whcValue?.value as string, window.navigator.language)} ${unit}`
    : t('global.analysis.noData');

  const whcBenchmarkType = useBenchmarkForFact(benchmarks, whcValue?.value);
  const dataPointHasLabel = whcHistoricValue && whcHistoricValue?.payload?.whc_historic?.labels?.length > 0;
  const dataPointValueClass = dataPointHasLabel
    ? whcValue?.payload.whc_historic?.labels?.find(
        (dataLabel: PlotReportFactLabel) => dataLabel.name === PlotReportFactLabelNameEnum.value_class,
      )
    : undefined;

  const dataLabel = dataPointValueClass?.value as ValueClass;

  // TODO: MVP-5260 - Bring Confidence Interval back to charts
  // const confidenceIntervalPct = whcScenarioValue
  //   ? whcValue?.payload?.whc?.confidence_interval_pct
  //   : whcValue?.payload?.whc_historic?.confidence_interval_pct;

  if (!active) return null;

  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printMonthYear(label)}</ChartTooltipTextSecondary>
        {dataLabel && <ValueClassPill title={dataLabel} />}
      </Stack>
      {whcBenchmarkType && (
        <ChartTooltipTextSecondary>
          <BenchmarkPill benchmarkType={whcBenchmarkType} />
        </ChartTooltipTextSecondary>
      )}
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextPrimary>{`${t('global.analysis.waterHoldingCapacity')}`}</ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{whcDisplay}</ChartTooltipTextPrimary>
      </Stack>
      {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
      {/* <ChartTooltipConfidenceInterval ciPct={confidenceIntervalPct} /> */}
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipWaterHoldingCapacityScenario };
